import { formatDateRange, plural } from '@trips/shared/format';
import type { TripSummary } from '@trips/shared/trip-model';
import { useMemo } from 'react';
import { selectNow, selectPrefs, selectTripLists } from '../../data/selectors';
import { useStore } from '../../data/store';
import { Link, navigate } from '../../lib/router';
import { Button, IconButton } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { Icon } from '../../ui/Icon';
import { NowCard } from './NowCard';
import s from './TripsScreen.module.css';

/** One trip: its name, its dates and how long it is. */
function TripRow({ trip, selected }: { trip: TripSummary; selected?: boolean }) {
  const prefs = useStore(selectPrefs);
  return (
    <Link
      to={`/trips/${trip.id}`}
      className={`${s.row} ${selected ? s.selected : ''}`}
      aria-current={selected ? 'page' : undefined}
    >
      <span className={s.rowText}>
        <span
          className={`${s.rowTitle} truncate`}
          style={{ viewTransitionName: `title-${trip.id}` }}
        >
          {trip.title}
        </span>
        <span className="t-footnote muted">
          {trip.startDate && trip.endDate
            ? formatDateRange(trip.startDate, trip.endDate, prefs, false)
            : 'No dates yet'}
        </span>
      </span>
      {trip.days > 0 && <span className={`${s.days} num`}>{plural(trip.days, 'day')}</span>}
    </Link>
  );
}

function PastList({ trips, selectedId }: { trips: TripSummary[]; selectedId?: string }) {
  const years = useMemo(() => {
    const out = new Map<string, TripSummary[]>();
    for (const t of trips) {
      const y = t.startDate?.slice(0, 4) ?? 'Undated';
      const list = out.get(y) ?? [];
      list.push(t);
      out.set(y, list);
    }
    return [...out.entries()];
  }, [trips]);
  return (
    <div className={s.years}>
      {years.map(([year, list]) => (
        <section key={year} className={s.year}>
          <h3 className={`${s.yearHead} t-subhead muted`}>{year}</h3>
          <div className={s.rows}>
            {list.map((t) => (
              <TripRow key={t.id} trip={t} selected={t.id === selectedId} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function TripsScreen({ pane, selectedId }: { pane?: boolean; selectedId?: string }) {
  const lists = useStore(selectTripLists);
  const nowState = useStore(selectNow);
  // The trip in the Now card isn't listed again below it.
  const shown = nowState.kind === 'on-trip' ? nowState.trip.id : null;
  const upcoming = [...lists.current, ...lists.upcoming].filter((t) => t.id !== shown);
  const empty = !lists.current.length && !lists.upcoming.length && !lists.past.length;

  return (
    <div className={`${s.screen} ${pane ? s.pane : ''}`}>
      <header className={s.header}>
        <h1 className="t-title1">Trips</h1>
        <div className={s.headerActions}>
          {!pane && (
            <>
              <IconButton
                icon="magnifying-glass"
                label="Search"
                onClick={() => navigate('/search')}
              />
              <IconButton
                icon="map-trifold"
                label="Map of all trips"
                onClick={() => navigate('/map')}
              />
              <IconButton icon="gear-six" label="Settings" onClick={() => navigate('/settings')} />
            </>
          )}
          <button
            type="button"
            className={s.add}
            aria-label="Add a plan"
            onClick={() => navigate('/new', { overlay: true })}
          >
            <Icon name="plus" size={20} />
          </button>
        </div>
      </header>

      {empty ? (
        <EmptyState
          icon="suitcase-rolling"
          title="Plan your next trip"
          action={
            <Button
              variant="primary"
              icon="plus"
              onClick={() => navigate('/new', { overlay: true })}
            >
              Add plan
            </Button>
          }
        >
          <p>Add a flight, stay or reservation and it lands here, grouped into a trip.</p>
        </EmptyState>
      ) : (
        <>
          <NowCard />
          {upcoming.length > 0 && (
            <section className={s.section}>
              <h2 className={s.sectionHead}>Upcoming</h2>
              <div className={s.rows}>
                {upcoming.map((t) => (
                  <TripRow key={t.id} trip={t} selected={t.id === selectedId} />
                ))}
              </div>
            </section>
          )}
          {lists.past.length > 0 && <PastList trips={lists.past} selectedId={selectedId} />}
        </>
      )}
    </div>
  );
}
